import { useState, useCallback, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { PanelLeft, Plus, Pencil } from 'lucide-react'
import { ContentDChat } from './ContentDChat'
import { ContentDDocumentPanel } from './ContentDDocumentPanel'
import { ContentDSessionSidebar } from './ContentDSessionSidebar'
import { ContentDRenameSessionModal } from './ContentDRenameSessionModal'
import { ContentDFoolscapPicker } from './ContentDFoolscapPicker'
import { ContentDMobileNav } from './ContentDMobileNav'
import { ResizableDivider } from '@/features/develop/components/ResizableDivider'
import { useContentDStore } from '@/stores/useContentDStore'
import { useAuthStore } from '@/stores/useAuthStore'
import { useIsMobile } from '@/hooks/useMediaQuery'
import { cn } from '@/lib/utils'

const MIN_PANEL_WIDTH = 300
const MAX_PANEL_WIDTH = 800
const DEFAULT_PANEL_WIDTH = 500
const DEFAULT_PANEL_WIDTH_WITH_SIDEBAR = 400  // Narrower when sidebar is open
const SIDEBAR_WIDTH = 260

// Storage key for tracking which user's data is in localStorage
const CONTENT_D_USER_ID_KEY = 'content_d-store-user-id'

export function ContentDView() {
  const {
    sessionName,
    mobileView,
    showSessionSidebar,
    toggleSessionSidebar,
    closeSessionSidebar,
    loadSessionFromDb,
    renameSession,
    setMobileView,
    startNewSession,
    openFoolscapPicker,
    createSessionFromFoolscap,
    fetchAvailableFoolscaps,
  } = useContentDStore()

  const [searchParams, setSearchParams] = useSearchParams()
  const isMobile = useIsMobile()
  const [showRenameModal, setShowRenameModal] = useState(false)
  const [isVerifyingUser, setIsVerifyingUser] = useState(true)  // Block UI until user verified

  // Get auth state - need to wait for auth initialization before creating sessions
  const { initialized: authInitialized, session: authSession, initialize: initializeAuth } = useAuthStore()

  const hasInitialized = useRef(false)
  const hasHandledFoolscapParam = useRef(false)

  // Initialize auth on mount
  useEffect(() => {
    initializeAuth()
  }, [initializeAuth])

  // CRITICAL: Verify user ownership BEFORE showing any content
  // This runs early and blocks UI rendering until verification completes
  useEffect(() => {
    if (!authInitialized) return
    if (!authSession?.user?.id) {
      // Not logged in - redirect will happen in the other effect
      setIsVerifyingUser(false)
      return
    }

    const currentUserId = authSession.user.id
    const storedUserId = localStorage.getItem(CONTENT_D_USER_ID_KEY)

    console.log('[ContentDView] Verifying user. Current:', currentUserId, 'Stored:', storedUserId)

    // If stored data belongs to different user, clear it IMMEDIATELY
    if (storedUserId && storedUserId !== currentUserId) {
      console.log('[ContentDView] User mismatch! Clearing stale data before render')
      console.log('[ContentDView] Stored:', storedUserId, 'Current:', currentUserId)

      // Store new user ID FIRST
      localStorage.setItem(CONTENT_D_USER_ID_KEY, currentUserId)

      // Clear localStorage content_d-store
      localStorage.removeItem('content_d-store')

      // Also use Zustand's persist API to clear storage if available
      // This ensures the persist middleware doesn't write stale data back
      const persistApi = (useContentDStore as unknown as { persist?: { clearStorage: () => void } }).persist
      if (persistApi?.clearStorage) {
        console.log('[ContentDView] Calling persist.clearStorage()')
        persistApi.clearStorage()
      }

      // Reset in-memory store - this clears all session data
      startNewSession()

      // Force a rehydration from the now-empty localStorage
      const rehydrate = (useContentDStore as unknown as { persist?: { rehydrate: () => Promise<void> } }).persist?.rehydrate
      if (rehydrate) {
        console.log('[ContentDView] Calling persist.rehydrate()')
        rehydrate()
      }

      // Reset initialization so we create a new session
      hasInitialized.current = false
    } else if (!storedUserId) {
      // First time for this user
      console.log('[ContentDView] First time for user, storing ID')
      localStorage.setItem(CONTENT_D_USER_ID_KEY, currentUserId)
    } else {
      console.log('[ContentDView] User IDs match, no action needed')
    }

    // Verify the store is clean - double check that no stale data remains
    const currentState = useContentDStore.getState()
    console.log('[ContentDView] Final verification - sessionId:', currentState.sessionId)
    console.log('[ContentDView] Final verification - documentContent length:', currentState.documentContent?.length)
    console.log('[ContentDView] Final verification - gatekeeperTurns count:', currentState.gatekeeperTurns?.length)

    // User verified, allow rendering
    setIsVerifyingUser(false)
  }, [authInitialized, authSession?.user?.id, startNewSession])

  // Handle foolscap query parameter - auto-create session from linked Content Foolscap
  useEffect(() => {
    const foolscapSessionId = searchParams.get('foolscap')
    if (!foolscapSessionId || hasHandledFoolscapParam.current) return
    if (!authInitialized || isVerifyingUser || !authSession) return

    hasHandledFoolscapParam.current = true

    const handleFoolscapParam = async () => {
      console.log('[ContentDView] Found foolscap param:', foolscapSessionId)

      // Fetch available foolscaps to get the name
      await fetchAvailableFoolscaps()
      const foolscaps = useContentDStore.getState().availableFoolscaps
      const targetFoolscap = foolscaps.find(f => f.session_id === foolscapSessionId)

      if (targetFoolscap) {
        console.log('[ContentDView] Auto-creating session from Content Foolscap:', targetFoolscap.name || targetFoolscap.entity_name)
        // Gatekeeper will gather document preferences conversationally
        await createSessionFromFoolscap(
          foolscapSessionId,
          targetFoolscap.name || targetFoolscap.entity_name || undefined
        )
        // Clear the query param after creating session
        setSearchParams({}, { replace: true })
        hasInitialized.current = true  // Prevent the other effect from opening picker
      } else {
        console.log('[ContentDView] Content Foolscap not found, opening picker')
        openFoolscapPicker()
        setSearchParams({}, { replace: true })
      }
    }

    handleFoolscapParam()
  }, [searchParams, authInitialized, isVerifyingUser, authSession, fetchAvailableFoolscaps, createSessionFromFoolscap, openFoolscapPicker, setSearchParams])

  // On mount: wait for auth + user verification, then reload session from DB or create new
  useEffect(() => {
    // Prevent double initialization
    if (hasInitialized.current) return

    // Wait for auth to be initialized
    if (!authInitialized) {
      console.log('[ContentDView] Waiting for auth initialization...')
      return
    }

    // Wait for user verification to complete
    if (isVerifyingUser) {
      console.log('[ContentDView] Waiting for user verification...')
      return
    }

    // Check if user is authenticated
    if (!authSession) {
      console.log('[ContentDView] No auth session, redirecting to login')
      window.location.href = '/content_d/login?returnTo=' + encodeURIComponent(window.location.pathname)
      return
    }

    // If we have a foolscap param, let that effect handle initialization
    if (searchParams.get('foolscap')) {
      console.log('[ContentDView] Foolscap param present, deferring to that handler')
      return
    }

    // Check if Zustand has finished hydrating from localStorage
    const checkAndInit = async () => {
      // Small delay to ensure Zustand hydration completes
      await new Promise(resolve => setTimeout(resolve, 50))

      // Get fresh state after hydration
      const state = useContentDStore.getState()
      const hydratedSessionId = state.sessionId

      if (hasInitialized.current) return  // Double-check after await
      hasInitialized.current = true

      if (hydratedSessionId) {
        console.log('[ContentDView] Loading session from DB:', hydratedSessionId)
        loadSessionFromDb(hydratedSessionId)
      } else {
        console.log('[ContentDView] No session found, creating new')
        openFoolscapPicker()
      }
    }

    checkAndInit()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authInitialized, authSession, isVerifyingUser, searchParams]) // Re-run when auth state changes

  // Use narrower document panel when sidebar is open to give chat more room
  const [documentWidth, setDocumentWidth] = useState(DEFAULT_PANEL_WIDTH)

  // Adjust document width when sidebar is toggled
  useEffect(() => {
    setDocumentWidth(showSessionSidebar ? DEFAULT_PANEL_WIDTH_WITH_SIDEBAR : DEFAULT_PANEL_WIDTH)
  }, [showSessionSidebar])

  const handleResize = useCallback((delta: number) => {
    setDocumentWidth(prev => {
      // Delta is positive when moving right (shrinking panel), negative when moving left (growing panel)
      const newWidth = prev - delta
      return Math.min(MAX_PANEL_WIDTH, Math.max(MIN_PANEL_WIDTH, newWidth))
    })
  }, [])

  // Handle session selection on mobile - switch to chat view
  const handleMobileSessionSelect = useCallback(() => {
    setMobileView('chat')
  }, [setMobileView])

  // CRITICAL: Block rendering until user verification completes
  // This prevents showing another user's data even briefly
  if (isVerifyingUser || !authInitialized) {
    return (
      <div className="h-dvh flex items-center justify-center bg-background">
        <div className="text-muted-foreground text-sm">Loading...</div>
      </div>
    )
  }

  // Mobile layout
  if (isMobile) {
    return (
      <div className="h-dvh flex flex-col bg-background overflow-hidden">
        {/* Mobile Header */}
        <header className="flex-shrink-0 h-14 border-b border-border">
          <div className="px-4 h-full flex items-center justify-between">
            {/* Left side: Session name */}
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <h1 className="text-sm font-medium text-foreground truncate">{sessionName}</h1>
              <button
                onClick={() => setShowRenameModal(true)}
                className="p-1.5 text-muted-foreground hover:text-foreground rounded transition-colors flex-shrink-0"
                title="Rename session"
              >
                <Pencil className="w-3.5 h-3.5" strokeWidth={1.5} />
              </button>
            </div>

            {/* Right side: New session button */}
            <button
              onClick={() => openFoolscapPicker()}
              className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded transition-colors"
              title="New session"
            >
              <Plus className="w-5 h-5" strokeWidth={1.5} />
            </button>
          </div>
        </header>

        {/* Main content area - single view at a time */}
        {/* pb-14 = 56px for nav height, additional safe-area handled by nav itself */}
        <main className="flex-1 overflow-hidden pb-14 pb-mobile-nav">
          {mobileView === 'sessions' && (
            <div className="h-full">
              <ContentDSessionSidebar
                onClose={() => setMobileView('chat')}
                onSessionSelect={handleMobileSessionSelect}
              />
            </div>
          )}
          {mobileView === 'chat' && (
            <div className="h-full">
              <ContentDChat />
            </div>
          )}
          {mobileView === 'document' && (
            <div className="h-full">
              <ContentDDocumentPanel />
            </div>
          )}
        </main>

        {/* Mobile bottom navigation */}
        <ContentDMobileNav />

        {/* Rename Session Modal */}
        <ContentDRenameSessionModal
          isOpen={showRenameModal}
          currentName={sessionName}
          onClose={() => setShowRenameModal(false)}
          onSubmit={(name) => {
            renameSession(name)
            setShowRenameModal(false)
          }}
        />

        {/* Foolscap Picker Modal */}
        <ContentDFoolscapPicker />
      </div>
    )
  }

  // Desktop layout (existing)
  return (
    <div className="h-dvh flex flex-col bg-background overflow-hidden">
      {/* Header */}
      <header className="flex-shrink-0 h-14">
        <div className="max-w-7xl mx-auto px-6 md:px-8 h-full flex items-center justify-between">
          {/* Left side: Toggle and New Session icons */}
          <div className="flex items-center gap-1">
            <button
              onClick={toggleSessionSidebar}
              className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded transition-colors"
              title={showSessionSidebar ? "Hide sessions" : "Show sessions"}
            >
              <PanelLeft className="w-4 h-4" strokeWidth={1.5} />
            </button>
            <button
              onClick={() => openFoolscapPicker()}
              className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded transition-colors"
              title="New session"
            >
              <Plus className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </header>

      {/* Main content area */}
      <main className="flex-1 overflow-hidden">
        <div className="h-full max-w-7xl mx-auto px-6 md:px-8 flex">
          {/* Session Sidebar - inside the constrained width */}
          {showSessionSidebar && (
            <div
              className="flex-shrink-0 h-full border-r border-border"
              style={{ width: SIDEBAR_WIDTH }}
            >
              <ContentDSessionSidebar onClose={closeSessionSidebar} />
            </div>
          )}

          {/* Main content column - title, chat */}
          <div className={cn("flex-1 flex flex-col min-w-0", showSessionSidebar && "pl-4")}>
            {/* Session title - above chat */}
            <div className="flex-shrink-0 pt-3 pb-2 flex items-center gap-2 group">
              <h1 className="text-sm font-medium text-foreground">{sessionName}</h1>
              <button
                onClick={() => setShowRenameModal(true)}
                className="p-1 text-muted-foreground/0 group-hover:text-muted-foreground hover:!text-foreground rounded transition-colors"
                title="Rename session"
              >
                <Pencil className="w-3 h-3" strokeWidth={1.5} />
              </button>
            </div>

            {/* Chat pane */}
            <div className="flex-1 overflow-hidden">
              <ContentDChat />
            </div>
          </div>

          <ResizableDivider onResize={handleResize} />

          {/* Document pane */}
          <div
            className="flex-shrink-0 overflow-hidden"
            style={{ width: documentWidth }}
          >
            <ContentDDocumentPanel />
          </div>
        </div>
      </main>

      {/* Rename Session Modal */}
      <ContentDRenameSessionModal
        isOpen={showRenameModal}
        currentName={sessionName}
        onClose={() => setShowRenameModal(false)}
        onSubmit={(name) => {
          renameSession(name)
          setShowRenameModal(false)
        }}
      />

      {/* Foolscap Picker Modal */}
      <ContentDFoolscapPicker />
    </div>
  )
}
